import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getDirectoryHandle as getProjectDirHandle, saveDirectoryHandle as saveProjectDirHandle, removeDirectoryHandle as removeProjectDirHandle, walkDirectory } from '../utils/workingDirectory';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Printer, FileType2, FileText,
  FolderOpen, FolderSync, RefreshCw, X as XIcon,
  AlertTriangle, Copy,
  Image as ImageIcon, Shield, BookOpen,
  Upload, Download, Trash2, Lock, Check, FileKey
} from 'lucide-react';
import StructureTree from '../components/StructureNode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══ Parse a PMC XML file ═══ */
async function parsePmcFile(fileHandle) {
  try {
    const file = await fileHandle.getFile();
    const text = await file.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'application/xml');

    const pmCode = doc.querySelector('pmCode');
    const pmTitle = doc.querySelector('pmTitle');
    const issueInfo = doc.querySelector('pmIdent issueInfo');
    const lang = doc.querySelector('pmIdent language');

    const mic = pmCode?.getAttribute('modelIdentCode') || '';
    const pmIssuer = pmCode?.getAttribute('pmIssuer') || '';
    const pmNumber = pmCode?.getAttribute('pmNumber') || '';
    const pmVolume = pmCode?.getAttribute('pmVolume') || '';
    const issueNumber = issueInfo?.getAttribute('issueNumber') || '001';
    const inWork = issueInfo?.getAttribute('inWork') || '00';
    const langCode = lang?.getAttribute('languageIsoCode') || 'en';
    const countryCode = lang?.getAttribute('countryIsoCode') || 'US';

    const pmcFormatted = `PMC-${mic}-${pmIssuer}-${pmNumber}-${pmVolume}_${issueNumber}-${inWork}_${countryCode}-${langCode}`;
    const structure = parsePmEntries(doc.querySelectorAll('content > pmEntry'));

    let dmCount = 0, childPmCount = 0, sectionCount = 0;
    const countNodes = (nodes) => {
      nodes.forEach(n => {
        if (n.dmRef) dmCount++;
        if (n.pmRef) childPmCount++;
        if (n.children?.length > 0) sectionCount++;
        if (n.children) countNodes(n.children);
      });
    };
    countNodes(structure);

    return {
      fileName: fileHandle.name,
      title: pmTitle?.textContent?.trim() || fileHandle.name,
      pmcFormatted,
      issueNumber, inWork,
      structure,
      dmCount, childPmCount, sectionCount,
      rawXml: text,
    };
  } catch (err) {
    console.error('Failed to parse PMC:', fileHandle.name, err);
    return null;
  }
}

function parsePmEntries(entryNodes) {
  const result = [];
  if (!entryNodes) return result;
  entryNodes.forEach(entry => {
    const node = { title: '', dmRef: null, pmRef: null, children: [] };
    const titleEl = entry.querySelector(':scope > pmEntryTitle');
    if (titleEl) node.title = titleEl.textContent?.trim() || '';

    const dmRefEl = entry.querySelector(':scope > dmRef');
    if (dmRefEl) {
      const dmCode = dmRefEl.querySelector('dmCode');
      if (dmCode) {
        const parts = [
          dmCode.getAttribute('modelIdentCode') || '',
          dmCode.getAttribute('systemDiffCode') || '',
          dmCode.getAttribute('systemCode') || '',
          (dmCode.getAttribute('subSystemCode') || '0') + (dmCode.getAttribute('subSubSystemCode') || '0'),
          dmCode.getAttribute('assyCode') || '',
          (dmCode.getAttribute('disassyCode') || '') + (dmCode.getAttribute('disassyCodeVariant') || ''),
          (dmCode.getAttribute('infoCode') || '') + (dmCode.getAttribute('infoCodeVariant') || ''),
          dmCode.getAttribute('itemLocationCode') || '',
        ].filter(Boolean);
        node.dmRef = `DMC-${parts.join('-')}`;
      }
      const techName = dmRefEl.querySelector('dmTitle techName');
      const infoName = dmRefEl.querySelector('dmTitle infoName');
      if (techName) node.title = [techName.textContent, infoName?.textContent].filter(Boolean).join(' - ');
    }

    const pmRefEl = entry.querySelector(':scope > pmRef');
    if (pmRefEl) {
      const pmRefCode = pmRefEl.querySelector('pmCode');
      if (pmRefCode) {
        node.pmRef = `PMC-${pmRefCode.getAttribute('modelIdentCode') || ''}-${pmRefCode.getAttribute('pmNumber') || ''}`;
      }
    }

    const childEntries = entry.querySelectorAll(':scope > pmEntry');
    if (childEntries.length > 0) {
      node.children = parsePmEntries(childEntries);
    }
    result.push(node);
  });
  return result;
}

function collectDmCodes(structure) {
  const codes = [];
  const walk = (nodes) => {
    nodes.forEach(n => {
      if (n.dmRef) codes.push(n.dmRef);
      if (n.children) walk(n.children);
    });
  };
  walk(structure);
  return codes;
}

/* ═══ Read file as base64 data URI (FileReader — safe for any size) ═══ */
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error('File read failed'));
    r.readAsDataURL(blob);
  });
}

async function readFileAsBase64(fileHandle) {
  const file = await fileHandle.getFile();
  const dataUrl = await blobToDataUrl(file);
  if (!file.type) {
    // Normalise missing MIME to octet-stream
    return `data:application/octet-stream;base64,${dataUrl.split(',', 2)[1]}`;
  }
  return dataUrl;
}

/* ═══ Read file and return raw base64 (no data URI) ═══ */
async function readFileAsRawBase64(file) {
  const dataUrl = await blobToDataUrl(file);
  return dataUrl.split(',', 2)[1];
}


/* ═══════════════════════════════════════════════════════════════
   ADMIN STYLESHEET MANAGEMENT PANEL
   ═══════════════════════════════════════════════════════════════ */
const AdminStylesheetPanel = ({ token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [stylesheets, setStylesheets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', description: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        axios.get(`${API}/pdf-publish/stylesheets`, { headers }),
        axios.get(`${API}/pdf-publish/companies`, { headers }),
      ]);
      setStylesheets(sRes.data);
      setCompanies(cRes.data);
    } catch { toast.error('Failed to load stylesheets'); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('xsl-upload-input');
    const file = fileInput?.files?.[0];
    if (!file) { toast.error('Select an XSL file'); return; }
    if (!form.name || !form.company) { toast.error('Name and company are required'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name);
      fd.append('company', form.company);
      fd.append('description', form.description);
      await axios.post(`${API}/pdf-publish/stylesheets/upload`, fd, { headers });
      toast.success('Stylesheet uploaded and encrypted');
      setShowUpload(false);
      setForm({ name: '', company: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const downloadEncrypted = async (id, origName) => {
    try {
      const res = await axios.get(`${API}/pdf-publish/stylesheets/${id}/download-encrypted`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = origName.replace(/\.xsl$/i, '') + '.enc';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Encrypted stylesheet downloaded');
    } catch { toast.error('Download failed'); }
  };

  const deleteStylesheet = async (id) => {
    if (!window.confirm('Delete this stylesheet?')) return;
    try {
      await axios.delete(`${API}/pdf-publish/stylesheets/${id}`, { headers });
      toast.success('Stylesheet deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="admin-stylesheet-panel">
      <div className="p-4 border-b border-amber-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <Lock className="w-4 h-4" /> Stylesheet Management
            <Badge className="bg-amber-100 text-amber-700 text-[10px]">Admin</Badge>
          </h2>
          <p className="text-xs text-amber-600 mt-0.5">Upload XSL stylesheets for customers. Downloads are auto-encrypted.</p>
        </div>
        <Button size="sm" onClick={() => setShowUpload(!showUpload)} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="toggle-upload-btn">
          <Upload className="w-3.5 h-3.5" /> Upload New
        </Button>
      </div>

      {showUpload && (
        <form onSubmit={handleUpload} className="p-4 border-b border-amber-100 bg-white/60 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Input placeholder="Stylesheet name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="stylesheet-name-input" />
            <Select value={form.company} onValueChange={v => setForm(f => ({ ...f, company: v }))}>
              <SelectTrigger data-testid="stylesheet-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>
                {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex items-center gap-3">
            <input type="file" id="xsl-upload-input" accept=".xsl,.xml,.xslt" className="text-sm" data-testid="xsl-file-input" />
            <Button type="submit" size="sm" disabled={uploading} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="upload-submit-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload &amp; Encrypt
            </Button>
          </div>
        </form>
      )}

      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
        ) : stylesheets.length === 0 ? (
          <div className="p-6 text-center text-sm text-amber-600">No stylesheets uploaded yet</div>
        ) : (
          <div className="divide-y divide-amber-100">
            {stylesheets.map(s => (
              <div key={s.id} className="p-3 flex items-center justify-between hover:bg-amber-50/50" data-testid={`stylesheet-row-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{s.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px]">{s.company}</Badge>
                    <span className="text-[10px] text-slate-400">{s.original_filename} ({(s.size_bytes / 1024).toFixed(1)}KB)</span>
                  </div>
                  {s.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{s.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-3">
                  <Button size="sm" variant="outline" onClick={() => downloadEncrypted(s.id, s.original_filename)}
                    className="h-7 px-2 text-xs gap-1 text-emerald-700 border-emerald-200" data-testid={`download-enc-${s.id}`}>
                    <Download className="w-3 h-3" /> Encrypted
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteStylesheet(s.id)}
                    className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-style-${s.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


/* ═══════════════════════════════════════════════════════════════
   MAIN PDF PUBLISH PAGE
   ═══════════════════════════════════════════════════════════════ */
const PDFPublishPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user: authUser } = useAuth();
  const projects = useProjects();
  const projectState = location.state?.project;
  const [selectedProjectId, setSelectedProjectId] = useState(projectState?.id || 'none');
  const currentProject = projects.find(p => p.id === selectedProjectId);
  const projectId = selectedProjectId !== 'none' ? selectedProjectId : null;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = authUser?.role === 'admin';

  // Working directory
  const [dirHandle, setDirHandle] = useState(null);
  const [dirName, setDirName] = useState('');
  const [scanningDir, setScanningDir] = useState(false);

  // PMC data
  const [pmcFiles, setPmcFiles] = useState([]);
  const [selectedPm, setSelectedPm] = useState(null);
  const [generating, setGenerating] = useState('');

  // Custom stylesheet state
  const [customXslFile, setCustomXslFile] = useState(null); // File object for encrypted .enc
  const [customXslName, setCustomXslName] = useState('');
  const [assetImages, setAssetImages] = useState({}); // { logo: File, warning: File, ... }

  // Server-side stylesheets for customers
  const [serverStylesheets, setServerStylesheets] = useState([]);
  const [selectedStylesheetId, setSelectedStylesheetId] = useState('');

  // Fetch available stylesheets for current user
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/pdf-publish/stylesheets`, { headers })
      .then(r => setServerStylesheets(r.data))
      .catch(() => {});
  }, [token, headers]);

  // Scan directory for Publication Module files.
  // A file qualifies if it is named PMC-*.xml OR its content has a <pm> root element
  // (so PMs are found regardless of file naming).
  const scanDirectory = useCallback(async (handle) => {
    if (!handle) return;
    setScanningDir(true);
    try {
      const pmFiles = [];
      for await (const { entry } of walkDirectory(handle)) {
        if (!entry.name.toLowerCase().endsWith('.xml')) continue;
        if (entry.name.toUpperCase().startsWith('PMC-')) { pmFiles.push(entry); continue; }
        try {
          const f = await entry.getFile();
          const snippet = await f.slice(0, 8192).text();
          if (/<pm[\s>]/.test(snippet)) pmFiles.push(entry);
        } catch { /* unreadable file — skip */ }
      }
      const parsed = [];
      for (const fh of pmFiles) {
        const pm = await parsePmcFile(fh);
        if (pm) parsed.push(pm);
      }
      parsed.sort((a, b) => a.title.localeCompare(b.title));
      setPmcFiles(parsed);
    } catch {
      setPmcFiles([]);
      toast.error('Failed to scan directory');
    }
    setScanningDir(false);
  }, []);

  // Load the working directory linked to the SELECTED PROJECT (Project Settings store)
  useEffect(() => {
    (async () => {
      setDirHandle(null); setPmcFiles([]); setDirName(''); setSelectedPm(null);
      if (!projectId) return;
      try {
        const record = await getProjectDirHandle(projectId);
        const saved = record?.handle;
        if (saved) {
          setDirHandle(saved);
          setDirName(record.displayName || saved.name);
          const perm = await saved.queryPermission({ mode: 'read' });
          if (perm === 'granted') await scanDirectory(saved);
        }
      } catch { /* no saved handle for this project */ }
    })();
  }, [projectId, scanDirectory]);

  const linkDirectory = async () => {
    if (!window.showDirectoryPicker) {
      toast.error('Use Chrome or Edge for File System Access API support.');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ mode: 'read' });
      setDirHandle(handle);
      setDirName(handle.name);
      await scanDirectory(handle);
      if (projectId) {
        try {
          await saveProjectDirHandle(projectId, handle, handle.name);
          await axios.put(`${API}/projects/${projectId}`, { working_directory: handle.name }, { headers: { Authorization: `Bearer ${token}` } });
        } catch { /* non-critical */ }
      }
      toast.success(`Linked to "${handle.name}"`);
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Failed to access directory');
    }
  };

  const refreshDir = async () => {
    if (!dirHandle) return;
    try {
      let perm = await dirHandle.queryPermission({ mode: 'read' });
      if (perm !== 'granted') {
        perm = await dirHandle.requestPermission({ mode: 'read' });
        if (perm !== 'granted') { toast.error('Permission denied'); return; }
      }
      await scanDirectory(dirHandle);
      toast.success('Directory refreshed');
    } catch {
      toast.error('Directory access lost');
      setDirHandle(null); setPmcFiles([]); setDirName('');
    }
  };

  const unlinkDir = async () => {
    setDirHandle(null); setPmcFiles([]); setDirName(''); setSelectedPm(null);
    if (projectId) {
      try {
        await removeProjectDirHandle(projectId);
        await axios.put(`${API}/projects/${projectId}`, { working_directory: '' }, { headers: { Authorization: `Bearer ${token}` } });
      } catch { /* non-critical */ }
    }
    toast.info('Working directory unlinked');
  };

  /* ═══ Standard PDF (reportlab) generation ═══ */
  const generatePdf = async (withIdStatus) => {
    if (!selectedPm) { toast.error('Select a Publication Module first'); return; }
    if (!dirHandle) { toast.error('Working directory not linked'); return; }

    const type = withIdStatus ? 'with-id' : 'without-id';
    setGenerating(type);
    toast.info('Reading data modules from working directory...');

    try {
      const dmCodes = collectDmCodes(selectedPm.structure);
      if (dmCodes.length === 0) { toast.error('No data module references found'); setGenerating(''); return; }

      const dirFileMap = {};
      for await (const { entry } of walkDirectory(dirHandle)) {
        if (!(entry.name.toUpperCase() in dirFileMap)) dirFileMap[entry.name.toUpperCase()] = entry;
      }

      const dmModules = [];
      const allIcns = new Set();
      let foundCount = 0;

      for (const dmc of dmCodes) {
        const dmcUpper = dmc.toUpperCase();
        let fileHandle = null;
        for (const [fname, handle] of Object.entries(dirFileMap)) {
          if (fname.startsWith(dmcUpper) && fname.endsWith('.XML')) { fileHandle = handle; break; }
        }

        if (!fileHandle) {
          dmModules.push({ dmc, title: `(File not found: ${dmc})`, issue_number: '001', in_work: '00', security: '01', quality_assurance: 'Unverified', schema_type: 'descriptive', levelled_paras: [] });
          continue;
        }

        foundCount++;
        const file = await fileHandle.getFile();
        const text = await file.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'application/xml');

        const issueInfo = doc.querySelector('issueInfo');
        const secEl = doc.querySelector('security');
        const qaEl = doc.querySelector('qualityAssurance');
        const titleEl = doc.querySelector('dmTitle techName');
        const infoEl = doc.querySelector('dmTitle infoName');

        const dmTitle = [titleEl?.textContent, infoEl?.textContent].filter(Boolean).join(' - ') || dmc;
        const issueNumber = issueInfo?.getAttribute('issueNumber') || '001';
        const inWork = issueInfo?.getAttribute('inWork') || '00';
        const security = secEl?.getAttribute('securityClassification') || '01';
        let qa = 'Unverified';
        if (qaEl?.querySelector('firstVerification')) qa = 'First verification';
        else if (qaEl?.querySelector('secondVerification')) qa = 'Second verification';

        const hasProc = doc.querySelector('content procedure');
        const schemaType = hasProc ? 'procedural' : 'descriptive';
        const levelledParas = [];
        const lpSelector = hasProc ? 'content procedure mainProcedure proceduralStep' : 'content description levelledPara';
        const lpElements = doc.querySelectorAll(lpSelector);

        lpElements.forEach(lpEl => {
          const lp = { id: lpEl.getAttribute('id') || '', title: '', elements: [] };
          const titleNode = lpEl.querySelector(':scope > title');
          if (titleNode) lp.title = titleNode.textContent?.trim() || '';

          for (const child of lpEl.children) {
            if (child.tagName === 'title') continue;
            if (child.tagName === 'para') {
              const nestedList = child.querySelector(':scope > randomList') || child.querySelector(':scope > sequentialList');
              let paraText = '';
              for (const nd of child.childNodes) { if (nd.nodeType === 3) paraText += nd.textContent; }
              paraText = paraText.trim();
              if (nestedList) {
                const listType = child.querySelector(':scope > randomList') ? 'randomList' : 'sequentialList';
                const items = [];
                nestedList.querySelectorAll(':scope > listItem > para').forEach(li => items.push(li.textContent || ''));
                lp.elements.push({ type: 'para', content: paraText, inlineList: { type: listType, items } });
              } else {
                lp.elements.push({ type: 'para', content: child.textContent?.trim() || '' });
              }
            } else if (child.tagName === 'randomList' || child.tagName === 'sequentialList') {
              const items = [];
              child.querySelectorAll(':scope > listItem > para').forEach(li => items.push(li.textContent || ''));
              lp.elements.push({ type: child.tagName, items });
            } else if (child.tagName === 'note') {
              const np = child.querySelector('notePara');
              if (np) lp.elements.push({ type: 'note', content: np.textContent || '' });
            } else if (child.tagName === 'warning') {
              const wp = child.querySelector('warningAndCautionPara');
              if (wp) lp.elements.push({ type: 'warning', content: wp.textContent || '' });
            } else if (child.tagName === 'caution') {
              const cp = child.querySelector('warningAndCautionPara');
              if (cp) lp.elements.push({ type: 'caution', content: cp.textContent || '' });
            } else if (child.tagName === 'figure') {
              const figTitle = child.querySelector(':scope > title');
              const graphic = child.querySelector(':scope > graphic');
              const icn = graphic?.getAttribute('infoEntityIdent') || '';
              lp.elements.push({ type: 'figure', title: figTitle?.textContent || '', icn });
              if (icn) allIcns.add(icn);
            }
          }
          if (lp.elements.length > 0 || lp.title) levelledParas.push(lp);
        });

        doc.querySelectorAll('content description > figure, content procedure > figure').forEach(fig => {
          const graphic = fig.querySelector(':scope > graphic');
          const icn = graphic?.getAttribute('infoEntityIdent') || '';
          if (icn) allIcns.add(icn);
        });

        dmModules.push({ dmc, title: dmTitle, issue_number: issueNumber, in_work: inWork, security, quality_assurance: qa, schema_type: schemaType, levelled_paras: levelledParas });
      }

      toast.info(`Found ${foundCount}/${dmCodes.length} DM files. Reading ${allIcns.size} image(s)...`);

      const images = {};
      for (const icn of allIcns) {
        const icnUpper = icn.toUpperCase();
        for (const ext of ['.PNG', '.JPG', '.JPEG', '.GIF', '.TIF', '.TIFF', '.SVG', '.CGM', '']) {
          const tryName = ext ? `${icnUpper}${ext}` : icnUpper;
          const imgHandle = dirFileMap[tryName];
          if (imgHandle) {
            try {
              const imgFile = await imgHandle.getFile();
              const dataUrl = await blobToDataUrl(imgFile);
              images[icn] = imgFile.type ? dataUrl : `data:image/png;base64,${dataUrl.split(',', 2)[1]}`;
            } catch { /* skip unreadable */ }
            break;
          }
        }
      }

      toast.info(`Generating PDF with ${dmModules.length} DM(s) and ${Object.keys(images).length} image(s)...`);

      const res = await axios.post(`${API}/pdf-publish/generate-from-dir`, {
        pm_title: selectedPm.title,
        pmc_code: selectedPm.pmcFormatted,
        include_id_status: withIdStatus,
        dm_modules: dmModules,
        images,
        project_id: projectId || '',
      }, { headers, responseType: 'blob', timeout: 120000 });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${(selectedPm.title || 'publication').replace(/\s+/g, '_')}_${withIdStatus ? 'IDStatus' : 'NoIDStatus'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF generated and downloaded');
    } catch (err) {
      console.error('PDF generation error:', err);
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const detail = JSON.parse(text).detail;
          toast.error(typeof detail === 'string' ? detail : 'PDF generation failed');
        } catch { toast.error(`PDF generation failed (HTTP ${err.response.status})`); }
      } else if (err.response?.status === 413) {
        toast.error('PDF generation failed: images too large to upload. Try reducing image file sizes.');
      } else {
        toast.error(err.message || 'PDF generation failed');
      }
    }
    setGenerating('');
  };

  /* ═══ Custom XSL-FO PDF generation ═══ */
  const generateCustomPdf = async () => {
    if (!selectedPm) { toast.error('Select a Publication Module first'); return; }
    if (!dirHandle) { toast.error('Working directory not linked'); return; }
    if (!customXslFile && !selectedStylesheetId) {
      toast.error('Upload an encrypted stylesheet or select one from the server');
      return;
    }

    setGenerating('custom');
    try {
      const dmCodes = collectDmCodes(selectedPm.structure);
      if (dmCodes.length === 0) { toast.error('No data module references found'); setGenerating(''); return; }

      toast.info('Reading PM and DM files from working directory...');

      // Build file map
      const dirFileMap = {};
      for await (const { entry } of walkDirectory(dirHandle)) {
        if (!(entry.name.toUpperCase() in dirFileMap)) dirFileMap[entry.name.toUpperCase()] = entry;
      }

      // Read raw DM XML files
      const dmXmls = {};
      const allIcns = new Set();
      let foundCount = 0;

      for (const dmc of dmCodes) {
        const dmcUpper = dmc.toUpperCase();
        let fileHandle = null;
        for (const [fname, handle] of Object.entries(dirFileMap)) {
          if (fname.startsWith(dmcUpper) && fname.endsWith('.XML')) { fileHandle = handle; break; }
        }
        if (!fileHandle) continue;

        foundCount++;
        const file = await fileHandle.getFile();
        const rawXml = await file.text();
        dmXmls[dmc] = rawXml;

        // Collect ICNs from the DM
        const parser = new DOMParser();
        const doc = parser.parseFromString(rawXml, 'application/xml');
        doc.querySelectorAll('graphic').forEach(g => {
          const icn = g.getAttribute('infoEntityIdent');
          if (icn) allIcns.add(icn);
        });
      }

      toast.info(`Found ${foundCount}/${dmCodes.length} DMs. Reading ${allIcns.size} image(s)...`);

      // Read ICN images as base64
      const images = {};
      for (const icn of allIcns) {
        const icnUpper = icn.toUpperCase();
        for (const ext of ['.PNG', '.JPG', '.JPEG', '.GIF', '.TIF', '.TIFF', '.SVG', '']) {
          const tryName = ext ? `${icnUpper}${ext}` : icnUpper;
          const imgHandle = dirFileMap[tryName];
          if (imgHandle) {
            try { images[icn] = await readFileAsBase64(imgHandle); } catch { /* skip */ }
            break;
          }
        }
      }

      // Read asset images
      const assetB64 = {};
      for (const [key, file] of Object.entries(assetImages)) {
        if (file) {
          try { assetB64[key] = `data:${file.type || 'image/png'};base64,${await readFileAsRawBase64(file)}`; } catch { /* skip */ }
        }
      }

      // Prepare encrypted XSL
      let encryptedXslB64 = null;
      let stylesheetId = null;

      if (customXslFile) {
        const raw = await readFileAsRawBase64(customXslFile);
        encryptedXslB64 = raw;
      } else if (selectedStylesheetId) {
        stylesheetId = selectedStylesheetId;
      }

      toast.info('Generating PDF with custom stylesheet...');

      const payload = {
        pm_xml: selectedPm.rawXml,
        dm_xmls: dmXmls,
        encrypted_xsl_b64: encryptedXslB64,
        stylesheet_id: stylesheetId,
        images,
        asset_images: assetB64,
        project_id: projectId || '',
      };

      const res = await axios.post(`${API}/pdf-publish/generate-xslfo`, payload, {
        headers, responseType: 'blob', timeout: 120000,
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${(selectedPm.title || 'publication').replace(/\s+/g, '_')}_custom.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Custom PDF generated and downloaded');
    } catch (err) {
      console.error('Custom PDF error:', err);
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const detail = JSON.parse(text).detail;
          toast.error(detail || 'Custom PDF generation failed');
        } catch { toast.error('Custom PDF generation failed'); }
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Custom PDF generation failed');
      }
    }
    setGenerating('');
  };

  const handleXslFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.enc,.xsl,.xslt';
    input.onchange = (e) => {
      const f = e.target.files?.[0];
      if (f) { setCustomXslFile(f); setCustomXslName(f.name); setSelectedStylesheetId(''); toast.success(`Loaded: ${f.name}`); }
    };
    input.click();
  };

  const handleAssetSelect = (assetKey, label) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const f = e.target.files?.[0];
      if (f) { setAssetImages(prev => ({ ...prev, [assetKey]: f })); toast.success(`${label}: ${f.name}`); }
    };
    input.click();
  };

  const copyXml = () => {
    if (selectedPm?.rawXml) {
      navigator.clipboard.writeText(selectedPm.rawXml);
      toast.success('XML copied to clipboard');
    }
  };

  const hasCustomXsl = !!customXslFile || !!selectedStylesheetId;

  // ═══ NO PROJECT SELECTED ═══
  if (!projectId) {
    return (
      <div className="space-y-6" data-testid="pdf-publish-page">
        <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between -mx-6 -mt-6 mb-6 rounded-t-lg">
          <Button variant="ghost" onClick={() => navigate(projectId ? `/dashboard/projects/${projectId}` : '/dashboard/projects')} className="text-white hover:bg-slate-800 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {projectId ? 'Back to Project' : 'Back to Projects'}
          </Button>
        </div>
        {isAdmin && <div className="max-w-4xl mx-auto mb-6"><AdminStylesheetPanel token={token} /></div>}
        <div className="max-w-xl mx-auto text-center space-y-6 py-12">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>PDF Publish</h1>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Pick a Publication Module, follow its structure (with all referenced DMs and child PMs pulled in), and export the whole thing to a single PDF.</p>
          <Select value="none" onValueChange={v => setSelectedProjectId(v)}>
            <SelectTrigger className="w-[320px] mx-auto" data-testid="pdf-publish-project-select">
              <SelectValue placeholder="Select a project..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select a project...</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  // ═══ MAIN LAYOUT ═══
  return (
    <div className="space-y-0" data-testid="pdf-publish-page">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-2.5 flex items-center justify-between -mx-6 -mt-6 mb-4 rounded-t-lg">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(projectId ? `/dashboard/projects/${projectId}` : '/dashboard/projects')} className="text-white hover:bg-slate-800 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Project
          </Button>
          <span className="text-slate-500">|</span>
          <span className="text-sm text-white font-semibold">PDF Publish</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-300">Project: <span className="text-white font-semibold">{currentProject?.title}</span></span>
          <Select value={selectedProjectId} onValueChange={v => { setSelectedProjectId(v); setSelectedPm(null); }}>
            <SelectTrigger className="w-[180px] h-8 bg-slate-800 border-slate-700 text-white text-xs" data-testid="pdf-publish-project-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {projectId && (dirName ? (
            <Badge variant="outline" className="bg-slate-800 text-violet-300 border-slate-600 gap-1.5 max-w-[220px]" data-testid="wd-chip">
              <FolderOpen className="w-3 h-3 shrink-0" /><span className="truncate font-mono text-xs">{dirName}</span>
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-slate-800 text-amber-300 border-slate-600 gap-1.5" data-testid="wd-chip">
              <FolderOpen className="w-3 h-3" /><span className="text-xs">No directory linked</span>
            </Badge>
          ))}
        </div>
      </div>

      {/* Description + working dir controls */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>PDF Publish</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-lg">Pick a Publication Module, follow its structure (with all referenced DMs and child PMs pulled in), and export the whole thing to a single PDF.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!dirHandle ? (
            <Button variant="outline" size="sm" onClick={linkDirectory} data-testid="link-dir-btn">
              <FolderOpen className="w-4 h-4 mr-2" /> Link Working Directory
            </Button>
          ) : (
            <>
              <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-xs">
                <FolderSync className="w-3.5 h-3.5 text-violet-600" />
                <span className="font-mono text-xs">{dirName}</span>
              </Badge>
              {pmcFiles.length === 0 && (
                <Button variant="outline" size="sm" onClick={refreshDir} className="gap-1.5 text-blue-600" data-testid="regrant-btn">
                  <RefreshCw className="w-3.5 h-3.5" /> Grant Access &amp; Scan
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={refreshDir} disabled={scanningDir} data-testid="refresh-dir-btn">
                {scanningDir ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="outline" size="sm" onClick={unlinkDir} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="unlink-dir-btn">
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Admin Stylesheet Management */}
      {isAdmin && <div className="mb-4"><AdminStylesheetPanel token={token} /></div>}

      {/* Two-panel layout */}
      <div className="grid grid-cols-12 gap-4" style={{ height: 'calc(100vh - 200px)' }}>
        {/* LEFT: Publication Modules list */}
        <div className="col-span-4 overflow-y-auto">
          <Card className="border-slate-200 h-full">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-700">Publication Modules</h2>
                <Badge className="bg-violet-100 text-violet-700 text-xs">{pmcFiles.length}</Badge>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Pick one to view its structure</p>
            </div>
            <CardContent className="p-0">
              {!dirHandle ? (
                <div className="p-8 text-center">
                  <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 font-medium">No working directory linked</p>
                  <p className="text-xs text-slate-400 mt-1">Link a working directory to load Publication Modules (PMC files)</p>
                </div>
              ) : scanningDir ? (
                <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : pmcFiles.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 font-medium">No Publication Modules found</p>
                  <p className="text-xs text-slate-400 mt-1">The working directory does not contain any Publication Module XML files (PMC-*.xml or files with a &lt;pm&gt; root element).</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {pmcFiles.map((pm, i) => {
                    const isSelected = selectedPm?.fileName === pm.fileName;
                    return (
                      <div
                        key={pm.fileName}
                        className={`p-3 cursor-pointer transition-colors ${isSelected ? 'bg-violet-50 border-l-3 border-l-violet-500' : 'hover:bg-slate-50'}`}
                        onClick={() => setSelectedPm(pm)}
                        data-testid={`pm-item-${i}`}
                      >
                        <p className="text-sm font-medium text-slate-800 truncate">{pm.title}</p>
                        <p className={`text-xs font-mono truncate mt-0.5 ${isSelected ? 'text-emerald-600' : 'text-slate-500'}`}>{pm.pmcFormatted}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="outline" className="text-[10px]">Iss {pm.issueNumber}/{pm.inWork}</Badge>
                          <Badge className="text-[10px] bg-blue-50 text-blue-600">
                            <FolderSync className="w-2.5 h-2.5 mr-0.5" /> Working dir
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: Structure & Actions */}
        <div className="col-span-8 overflow-y-auto space-y-4">

          {/* Custom Stylesheet Card — always visible */}
          <Card className="border-indigo-200 bg-indigo-50/20" data-testid="custom-stylesheet-card">
            <div className="p-4 border-b border-indigo-100">
              <h2 className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
                <FileKey className="w-4 h-4" /> Custom Stylesheet
              </h2>
              <p className="text-xs text-indigo-500 mt-0.5">Upload an encrypted stylesheet and custom images for branded PDF output</p>
            </div>
            <CardContent className="p-4 space-y-3">
              {/* Row 1: Stylesheet selection */}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleXslFileSelect} disabled={!!generating}
                  className={`gap-1.5 ${customXslFile ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'}`}
                  data-testid="custom-xsl-btn">
                  {customXslFile ? <Check className="w-3.5 h-3.5" /> : <FileKey className="w-3.5 h-3.5" />}
                  {customXslFile ? customXslName : 'Upload Encrypted Stylesheet (.enc)'}
                </Button>

                {serverStylesheets.length > 0 && (
                  <>
                    <span className="text-xs text-slate-400">or</span>
                    <Select value={selectedStylesheetId} onValueChange={v => { setSelectedStylesheetId(v); setCustomXslFile(null); setCustomXslName(''); }}>
                      <SelectTrigger className="w-[220px] h-8 text-xs border-indigo-200" data-testid="server-stylesheet-select">
                        <SelectValue placeholder="Select server stylesheet..." />
                      </SelectTrigger>
                      <SelectContent>
                        {serverStylesheets.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}

                {hasCustomXsl && (
                  <Button size="sm" variant="ghost" onClick={() => { setCustomXslFile(null); setCustomXslName(''); setSelectedStylesheetId(''); setAssetImages({}); }}
                    className="h-7 px-2 text-red-500 hover:bg-red-50" data-testid="clear-xsl-btn">
                    <XIcon className="w-3 h-3" />
                  </Button>
                )}
              </div>

              {/* Row 2: Custom images — always visible */}
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1.5">Custom Images</p>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'logo', label: 'Logo', icon: ImageIcon, color: 'blue' },
                    { key: 'warning', label: 'Warning', icon: AlertTriangle, color: 'red' },
                    { key: 'caution', label: 'Caution', icon: AlertTriangle, color: 'amber' },
                    { key: 'safety', label: 'Safety Notice', icon: Shield, color: 'emerald' },
                    { key: 'hazmat', label: 'Hazmat Border', icon: FileText, color: 'purple' },
                  ].map(({ key, label, icon: Icon, color }) => (
                    <Button key={key} size="sm" variant="outline" disabled={!!generating}
                      onClick={() => handleAssetSelect(key, label)}
                      className={`gap-1.5 text-xs transition-all ${
                        assetImages[key]
                          ? 'border-emerald-400 text-emerald-700 bg-emerald-50 shadow-sm'
                          : `border-slate-200 text-slate-500 hover:border-${color}-300 hover:text-${color}-600 hover:bg-${color}-50`
                      }`}
                      data-testid={`asset-${key}-btn`}>
                      {assetImages[key] ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                      {assetImages[key] ? <span>{label} <span className="text-[10px] font-normal opacity-70">({assetImages[key].name})</span></span> : label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Row 3: Generate button */}
              <div className="flex items-center gap-3 pt-1">
                <Button size="sm" onClick={generateCustomPdf}
                  disabled={!!generating || !hasCustomXsl || !selectedPm}
                  className="bg-indigo-600 hover:bg-indigo-700 gap-1.5 disabled:opacity-50" data-testid="generate-custom-btn">
                  {generating === 'custom' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                  Generate with Custom Stylesheet
                </Button>
                {!hasCustomXsl && <span className="text-xs text-slate-400">Load a stylesheet first</span>}
                {hasCustomXsl && !selectedPm && <span className="text-xs text-amber-600">Select a PM on the left to generate</span>}
              </div>
            </CardContent>
          </Card>

          {!selectedPm ? (
            <Card className="border-slate-200 flex items-center justify-center" style={{ minHeight: '200px' }}>
              <CardContent className="text-center py-12">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">Select a Publication Module on the left</p>
                <p className="text-sm text-slate-400 mt-1">Choose a PM to view its structure and publish options</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* PM Header + Standard Actions */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedPm.title}</h2>
                      <p className="text-xs font-mono text-emerald-600 mt-0.5">{selectedPm.pmcFormatted}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-700 text-xs">{selectedPm.dmCount} DMs</Badge>
                      <Badge className="bg-slate-100 text-slate-700 text-xs">{selectedPm.childPmCount} child PMs</Badge>
                      <Badge className="bg-slate-100 text-slate-700 text-xs">{selectedPm.sectionCount} sections</Badge>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button size="sm" onClick={() => generatePdf(true)} disabled={!!generating}
                      className="bg-teal-600 hover:bg-teal-700 gap-1.5" data-testid="print-with-id-btn">
                      {generating === 'with-id' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                      Print with IDStatus
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => generatePdf(false)} disabled={!!generating}
                      className="gap-1.5 border-teal-200 text-teal-700" data-testid="print-without-id-btn">
                      {generating === 'without-id' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileType2 className="w-3.5 h-3.5" />}
                      Print without IDStatus
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Structure Tree */}
              <Card className="border-slate-200">
                <div className="p-3 border-b border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Structure</h3>
                </div>
                <CardContent className="p-2 max-h-[35vh] overflow-y-auto" data-testid="pm-structure-tree">
                  {selectedPm.structure.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">No entries found in this PM</div>
                  ) : (
                    <StructureTree nodes={selectedPm.structure} />
                  )}
                </CardContent>
              </Card>

              {/* PM XML Preview */}
              <div className="bg-slate-900 rounded-lg overflow-hidden" data-testid="pm-xml-panel">
                <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 border-b border-slate-700">
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">PM XML</span>
                  <button onClick={copyXml} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid="copy-pm-xml-btn">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <pre className="p-4 text-xs font-mono text-green-400 leading-relaxed overflow-auto max-h-[30vh] whitespace-pre-wrap">
                  {selectedPm.rawXml?.substring(0, 3000) || '<!-- No XML -->'}
                  {selectedPm.rawXml?.length > 3000 && '\n\n<!-- ... truncated for display ... -->'}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFPublishPage;
